import { useMemo } from "react";

type Stage = {
  id: string;
  label: string;
  status: "pending" | "active" | "complete" | "error";
  message?: string;
};

type SectionProgress = {
  sectionId: string;
  title: string;
  status: "pending" | "generating" | "rendering" | "complete" | "error";
};

type StageProgressProps = {
  currentStage: string | null;
  stages?: Stage[];
  sections?: SectionProgress[];
  completedSections?: number;
  totalSections?: number;
};

const DEFAULT_STAGES: Stage[] = [
  { id: "preparation", label: "Preparation", status: "pending" },
  { id: "outline", label: "Outline", status: "pending" },
  { id: "storyboard", label: "Storyboard", status: "pending" },
  { id: "code", label: "Code Generation", status: "pending" },
  { id: "render", label: "Rendering", status: "pending" },
  { id: "merge", label: "Merge & Voiceover", status: "pending" },
];

export function StageProgress({
  currentStage,
  stages = DEFAULT_STAGES,
  sections = [],
  completedSections = 0,
  totalSections = 0,
}: StageProgressProps) {
  // Calculate stage statuses based on currentStage
  const computedStages = useMemo(() => {
    if (!currentStage) return stages;

    const stageOrder = stages.map((s) => s.id);
    const currentIndex = stageOrder.indexOf(currentStage.toLowerCase());

    return stages.map((stage, idx) => {
      if (idx < currentIndex) {
        return { ...stage, status: "complete" as const };
      } else if (idx === currentIndex) {
        return { ...stage, status: "active" as const };
      }
      return stage;
    });
  }, [currentStage, stages]);

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "complete":
        return "#66bb6a";
      case "active":
        return "#4a90d9";
      case "error":
        return "#ef5350";
      case "generating":
        return "#ffa726";
      case "rendering":
        return "#42a5f5";
      default:
        return "#666";
    }
  };

  const getStatusIcon = (status: string): string => {
    switch (status) {
      case "complete":
        return "✓";
      case "active":
      case "generating":
      case "rendering":
        return "●";
      case "error":
        return "✗";
      default:
        return "○";
    }
  };

  return (
    <div style={{ color: "#e0e0e0" }}>
      {/* Stage Progress Bar */}
      <div style={{ marginBottom: "1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "0.5rem" }}>
          {computedStages.map((stage, idx) => (
            <div
              key={stage.id}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
              }}
            >
              {/* Connector line */}
              {idx > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "12px",
                    left: "-50%",
                    right: "50%",
                    height: "2px",
                    backgroundColor:
                      computedStages[idx - 1].status === "complete" ? "#66bb6a" : "#333",
                  }}
                />
              )}

              {/* Stage circle */}
              <div
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  backgroundColor: stage.status === "pending" ? "#1a1a1a" : getStatusColor(stage.status),
                  border: `2px solid ${getStatusColor(stage.status)}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  zIndex: 1,
                  transition: "all 0.3s ease",
                  animation: stage.status === "active" ? "pulse 1.5s infinite" : "none",
                }}
              >
                {getStatusIcon(stage.status)}
              </div>

              {/* Label */}
              <span
                style={{
                  marginTop: "0.375rem",
                  fontSize: "0.65rem",
                  color: stage.status === "pending" ? "#666" : "#e0e0e0",
                  textAlign: "center",
                  fontWeight: stage.status === "active" ? 600 : 400,
                }}
              >
                {stage.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Section Progress (during render stage) */}
      {sections.length > 0 && (
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "0.5rem",
            }}
          >
            <span style={{ fontSize: "0.75rem", fontWeight: 500 }}>Sections</span>
            <span style={{ fontSize: "0.7rem", color: "#888" }}>
              {completedSections} / {totalSections} complete
            </span>
          </div>

          {/* Progress bar */}
          <div
            style={{
              height: "8px",
              backgroundColor: "#1a1a1a",
              borderRadius: "4px",
              overflow: "hidden",
              marginBottom: "0.75rem",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${totalSections > 0 ? (completedSections / totalSections) * 100 : 0}%`,
                backgroundColor: "#66bb6a",
                borderRadius: "4px",
                transition: "width 0.3s ease",
              }}
            />
          </div>

          {/* Section list */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.375rem" }}>
            {sections.map((section) => (
              <div
                key={section.sectionId}
                title={section.title}
                style={{
                  padding: "0.25rem 0.5rem",
                  backgroundColor:
                    section.status === "complete"
                      ? "#1b5e20"
                      : section.status === "error"
                      ? "#b71c1c"
                      : section.status === "generating" || section.status === "rendering"
                      ? "#1a237e"
                      : "#1a1a1a",
                  borderRadius: "4px",
                  fontSize: "0.7rem",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.25rem",
                  animation:
                    section.status === "generating" || section.status === "rendering"
                      ? "pulse 1.5s infinite"
                      : "none",
                }}
              >
                <span style={{ color: getStatusColor(section.status) }}>
                  {getStatusIcon(section.status)}
                </span>
                <span>{section.sectionId}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pulse animation */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.6; }
          }
        `}
      </style>
    </div>
  );
}

// Helper to determine stage from progress message
export function parseStageFromProgress(stage: string): string {
  const lowerStage = stage.toLowerCase();
  if (lowerStage.includes("prepar") || lowerStage.includes("pdf") || lowerStage.includes("ocr")) {
    return "preparation";
  }
  if (lowerStage.includes("outline")) {
    return "outline";
  }
  if (lowerStage.includes("storyboard")) {
    return "storyboard";
  }
  if (lowerStage.includes("code") || lowerStage.includes("generat")) {
    return "code";
  }
  if (lowerStage.includes("render")) {
    return "render";
  }
  if (lowerStage.includes("merge") || lowerStage.includes("voice") || lowerStage.includes("final")) {
    return "merge";
  }
  return stage;
}
